import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Flex,
  Text,
  HStack,
  VStack,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Heading,
  Icon,
  Input,
  Select,
  FormControl,
  FormLabel,
  useColorModeValue
} from '@chakra-ui/react';
import { AddIcon } from '@chakra-ui/icons';
import InvoiceList from '../components/InvoiceList';
import InvoiceModal from '../components/InvoiceModal';
import usePagination from '../utils/usePagination';
import api from '../api';

const SellInvoicesPage = () => {
  // Reduced useColorModeValue calls for better performance
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.400');

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null); // For displaying invoice details
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [invoiceCache, setInvoiceCache] = useState(new Map()); // Cache for detailed invoice data
  const [modalKey, setModalKey] = useState(Date.now()); // Force modal remount
  
  // Filter states
  const [filters, setFilters] = useState({
    search: '',
    start_date: '',
    end_date: '',
    total_min: '',
    total_max: '',
    status: ''
  });

  // Status translation mappings
  const statusOptions = [
    { value: '', label: 'Всі статуси' },
    { value: 'new', label: 'новий' },
    { value: 'pending', label: 'в очікуванні' },
    { value: 'completed', label: 'проведено' }
  ];

  const parseTotal = (value) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const totalWithoutVat = parseTotal(selectedInvoice?.total_without_vat || 0);
  const vat = parseTotal(selectedInvoice?.vat || 0);
  const totalWithVat = parseTotal(selectedInvoice?.total_with_vat || 0);

  const baseUrl = '/sell-invoices/';

  const fetchInvoices = async (url = `${baseUrl}?page=1`) => {
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams();
      
      // Add page parameter if it exists in the URL
      const urlObj = new URL(url, window.location.origin);
      const pageParam = urlObj.searchParams.get('page');
      if (pageParam) {
        params.append('page', pageParam);
      }
      
      // Add filter parameters
      if (filters.search) params.append('search', filters.search);
      if (filters.start_date) {
        params.append('start_date', filters.start_date);
      }
      if (filters.end_date) {
        params.append('end_date', filters.end_date);
      }
      if (filters.total_min) params.append('total_min', filters.total_min);
      if (filters.total_max) params.append('total_max', filters.total_max);
      if (filters.status) params.append('status', filters.status);
      
      // Construct the final URL
      const finalUrl = `${baseUrl}?${params.toString()}`;
      
      const response = await api.get(finalUrl);
      setInvoices(response.data.results);
      
      // Extract current page from the URL and update pagination
      const currentPage = parseInt(pageParam) || 1;
      updatePagination({
        ...response.data,
        currentPage: currentPage
      });
    } catch (error) {
      console.error('Failed to fetch invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const {
    pagination,
    updatePagination,
    fetchNext: originalFetchNext,
    fetchPrevious: originalFetchPrevious,
    goToPage: originalGoToPage,
  } = usePagination(fetchInvoices, baseUrl);

  // Custom pagination functions that preserve filters
  const buildPaginationUrl = (page) => {
    const params = new URLSearchParams();
    params.append('page', page);
    if (filters.search) params.append('search', filters.search);
    if (filters.start_date) {
      params.append('start_date', filters.start_date);
    }
    if (filters.end_date) {
      params.append('end_date', filters.end_date);
    }
    if (filters.total_min) params.append('total_min', filters.total_min);
    if (filters.total_max) params.append('total_max', filters.total_max);
    if (filters.status) params.append('status', filters.status);
    return `${baseUrl}?${params.toString()}`;
  };

  const fetchNext = () => {
    if (pagination.next) {
      const nextPage = pagination.currentPage + 1;
      fetchInvoices(buildPaginationUrl(nextPage));
    }
  };

  const fetchPrevious = () => {
    if (pagination.previous && pagination.currentPage > 1) {
      const prevPage = pagination.currentPage - 1;
      fetchInvoices(buildPaginationUrl(prevPage));
    }
  };

  const goToPage = (page) => {
    if (page >= 1) {
      fetchInvoices(buildPaginationUrl(page));
    }
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  // Trigger search when filters change (with debounce for search)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      // Always start from page 1 when filters change
      const params = new URLSearchParams();
      params.append('page', '1');
      if (filters.search) params.append('search', filters.search);
      if (filters.start_date) params.append('start_date', filters.start_date);
      if (filters.end_date) params.append('end_date', filters.end_date);
      if (filters.total_min) params.append('total_min', filters.total_min);
      if (filters.total_max) params.append('total_max', filters.total_max);
      if (filters.status) params.append('status', filters.status);
      
      const url = `${baseUrl}?${params.toString()}`;
      fetchInvoices(url);
    }, filters.search ? 300 : 0); // 300ms debounce for search, immediate for other filters

    return () => clearTimeout(timeoutId);
  }, [filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      start_date: '',
      end_date: '',
      total_min: '',
      total_max: '',
      status: ''
    });
  };

  const handleCreateClick = () => {
    // Completely reset modal state
    setIsCreateModalOpen(false);
    setEditingInvoice(null);
    setModalKey(Date.now()); // Force new modal instance
    
    // Open modal with clean state after a brief delay
    setTimeout(() => {
      setIsCreateModalOpen(true);
    }, 100);
  };

  const handleEditInvoice = async (invoiceId) => {
    try {
      // Force new modal instance for editing
      setModalKey(Date.now());
      
      // Check cache first
      if (invoiceCache.has(invoiceId)) {
        const cachedInvoice = invoiceCache.get(invoiceId);
        setEditingInvoice({
          ...cachedInvoice,
          total_without_vat: cachedInvoice.total_without_vat || 0,
          vat: cachedInvoice.vat || 0,
          total_with_vat: cachedInvoice.total_with_vat || 0,
        });
        setIsCreateModalOpen(true);
        return;
      }

      // Fallback to API call
      const response = await api.get(`${baseUrl}${invoiceId}/`);
      const invoiceData = response.data;
      
      // Cache the data
      setInvoiceCache(prev => new Map(prev.set(invoiceId, invoiceData)));
      
      setEditingInvoice({
        ...invoiceData,
        total_without_vat: invoiceData.total_without_vat || 0,
        vat: invoiceData.vat || 0,
        total_with_vat: invoiceData.total_with_vat || 0,
      });
      setIsCreateModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch updated invoice data:', error);
    }
  };
  

  const handleCloseModal = () => {
    setIsCreateModalOpen(false);
    // Clear editing invoice and force new modal key for next opening
    setTimeout(() => {
      setEditingInvoice(null);
      setModalKey(Date.now());
    }, 100);
  };

  const handleDeleteInvoice = async (id) => {
    if (!id) return;

    try {
      await api.delete(`${baseUrl}${id}/`);
      
      // Remove from cache
      setInvoiceCache(prev => {
        const newCache = new Map(prev);
        newCache.delete(id);
        return newCache;
      });
      
      // Close modal if deleted invoice is currently displayed
      if (selectedInvoice && selectedInvoice.id === id) {
        setIsDetailsModalOpen(false);
        setSelectedInvoice(null);
      }
      
      // Refresh the current page to maintain pagination and filters
      const currentUrl = buildPaginationUrl(pagination.currentPage > 1 ? pagination.currentPage : 1);
      fetchInvoices(currentUrl);
      
    } catch (error) {
      console.error('Failed to delete invoice:', error);
      // Fallback: refresh current page on error
      const currentUrl = buildPaginationUrl(pagination.currentPage > 1 ? pagination.currentPage : 1);
      fetchInvoices(currentUrl);
    }
  };

  const handleViewDetails = async (invoiceId) => {
    // Check if we have cached data for instant opening
    if (invoiceCache.has(invoiceId)) {
      const cachedInvoice = invoiceCache.get(invoiceId);
      setSelectedInvoice(cachedInvoice);
      setIsDetailsModalOpen(true);
      return;
    }

    // Simple approach like ReturnInvoicesPage - just fetch and open
    try {
      const response = await api.get(`${baseUrl}${invoiceId}/`);
      const detailedInvoice = response.data;
      
      // Cache for future use
      setInvoiceCache(prev => new Map(prev.set(invoiceId, detailedInvoice)));
      
      setSelectedInvoice(detailedInvoice);
      setIsDetailsModalOpen(true);
    } catch (error) {
      console.error('Failed to fetch invoice details:', error);
    }
  };

  const handleCloseDetailsModal = () => {
    setIsDetailsModalOpen(false);
    setSelectedInvoice(null);
    // Keep the cache intact for future use
  };

  const renderInvoiceForPrint = (invoiceData) => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write('<html><head><title>Накладна</title></head><body style="font-family: Arial, sans-serif; font-size: 12px;">');
    printWindow.document.write('<h1 style="text-align: center; margin-bottom: 20px; font-size: 18px;">Накладна #' + invoiceData.id + '</h1>');
    printWindow.document.write('<table border="1" cellpadding="5" cellspacing="0" style="width: 100%; border-collapse: collapse; border-color: #ddd;">');
    printWindow.document.write('<tr><td colspan="5" style="background-color: #f0f0f0; border-bottom: 1px solid #ddd; font-size: 14px;">');
    printWindow.document.write('<p style="margin-bottom: 10px;">Дата створення: ' + new Date(invoiceData.created_at).toLocaleString() + '</p>');
    printWindow.document.write('<p style="margin-bottom: 10px;">Примітка: ' + (invoiceData.note || 'Без приміток') + '</p>');
    printWindow.document.write('<p style="margin-bottom: 10px;">Клієнт: ' + (invoiceData.customer_name || 'Без клієнта') + '</p>');
    printWindow.document.write('</td></tr>');
    printWindow.document.write('<tr style="background-color: #f0f0f0; border-bottom: 1px solid #ddd;"><th style="width: 10%;">№</th><th style="width: 40%;">Назва товару</th><th style="width: 15%;">Ціна</th><th style="width: 15%;">Кількість</th><th style="width: 20%;">Сума</th></tr>');
    
    invoiceData.entries.forEach((entry, index) => {
      const price = parseFloat(entry.price);
      printWindow.document.write(
        `<tr style="border-bottom: 1px solid #ddd;"><td style="text-align: center;">${index + 1}</td><td>${entry.product.name}</td><td style="text-align: right;">${price.toFixed(2)} грн.</td><td style="text-align: center;">${entry.quantity}</td><td style="text-align: right;">${(price * entry.quantity).toFixed(2)} грн.</td></tr>`
      );
    });
    
    printWindow.document.write('</table>');
    printWindow.document.write('<div style="float: right; margin-top: 20px;">');
    printWindow.document.write('<p style="margin-bottom: 10px;">Разом без ПДВ: ' + invoiceData.total_without_vat + ' грн.</p>');
    printWindow.document.write('<p style="margin-bottom: 10px;">ПДВ: 0.00 грн.</p>');
    printWindow.document.write('<p style="margin-bottom: 10px;">Всього з ПДВ: ' + invoiceData.total_without_vat + ' грн.</p>');
    printWindow.document.write('</div>');
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };
  
  const handlePrintInvoice = async (invoiceId) => {
    // Check if we have detailed data in cache
    if (invoiceCache.has(invoiceId)) {
      renderInvoiceForPrint(invoiceCache.get(invoiceId));
      return;
    }

    // Check if we have invoice data with entries in the list
    const existingInvoice = invoices.find(inv => inv.id === invoiceId);
    if (existingInvoice && existingInvoice.entries && existingInvoice.entries.length > 0) {
      // Cache the data for future use
      setInvoiceCache(prev => new Map(prev.set(invoiceId, existingInvoice)));
      renderInvoiceForPrint(existingInvoice);
      return;
    }

    // Fetch from API if not cached
    try {
      const response = await api.get(`${baseUrl}${invoiceId}/`);
      const detailedInvoice = response.data;
      
      // Cache the detailed data
      setInvoiceCache(prev => new Map(prev.set(invoiceId, detailedInvoice)));
      
      renderInvoiceForPrint(detailedInvoice);
    } catch (error) {
      console.error('Failed to print invoice:', error);
    }
  };

  const handleChangeStatus = async (invoiceId, newStatus) => {
    try {
      await api.patch(`${baseUrl}${invoiceId}/`, { status: newStatus });
      
      // Update the status in the current list without changing pagination
      setInvoices(prevInvoices => 
        prevInvoices.map(invoice => 
          invoice.id === invoiceId 
            ? { ...invoice, status: newStatus, updated_at: new Date().toISOString() }
            : invoice
        )
      );
      
      // Update cache if it exists
      setInvoiceCache(prev => {
        if (prev.has(invoiceId)) {
          const cachedInvoice = prev.get(invoiceId);
          const newCache = new Map(prev);
          newCache.set(invoiceId, { ...cachedInvoice, status: newStatus });
          return newCache;
        }
        return prev;
      });
      
    } catch (error) {
      console.error('Failed to change status:', error);
      // On error, refresh current page with filters
      const currentUrl = buildPaginationUrl(pagination.currentPage > 1 ? pagination.currentPage : 1);
      fetchInvoices(currentUrl);
    }
  };

  const handleInvoiceCreated = async (invoiceData, isUpdate = false) => {
    // Close the modal and clear editing state
    setIsCreateModalOpen(false);
    setTimeout(() => {
      setEditingInvoice(null);
    }, 100);
    
    // Add to cache immediately
    setInvoiceCache(prev => new Map(prev.set(invoiceData.id, invoiceData)));
    
    if (isUpdate) {
      // For updates, update the existing invoice in the list without changing pagination
      setInvoices(prevInvoices => 
        prevInvoices.map(invoice => 
          invoice.id === invoiceData.id 
            ? { ...invoice, ...invoiceData, updated_at: new Date().toISOString() }
            : invoice
        )
      );
    } else {
      // For new invoices, refresh the current page to show changes with filters
      const currentUrl = buildPaginationUrl(pagination.currentPage > 1 ? pagination.currentPage : 1);
      fetchInvoices(currentUrl);
    }
  };  

  return (
    <Box position="relative" minH="100vh" bg={useColorModeValue('gray.50', 'gray.900')} overflowX="hidden">
      <Box position="relative" zIndex={1} p={{ base: 2, md: 8 }}>
        {/* Modern Header Card */}
        <Box
          bg={bgColor}
          borderRadius={{ base: 'lg', md: '2xl' }}
          border="1px solid"
          borderColor={borderColor}
          p={{ base: 3, md: 8 }}
          mb={{ base: 4, md: 8 }}
          boxShadow="0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
          position="relative"
          overflow="visible"
        >
          {/* Decorative gradient overlay - Blue theme for invoices */}
          <Box position="absolute" top={0} left={0} right={0} height="6px" bg="linear-gradient(90deg, #3B82F6, #2563EB, #1D4ED8)" />
          {/* Header section with icon and title */}
          <Flex justify="space-between" align="center" wrap="wrap" gap={4} direction={{ base: 'column', md: 'row' }}>
            <Flex align="center">
              <Box p={3} bg="linear-gradient(135deg, #3B82F6, #2563EB)" borderRadius="lg" mr={4} boxShadow="0 4px 6px -1px rgba(59, 130, 246, 0.3)">
                <Icon viewBox="0 0 24 24" boxSize={6} color="white">
                  <path fill="currentColor" d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z"/>
                </Icon>
              </Box>
              <Box>
                <Heading as="h1" size={{ base: 'lg', md: '2xl' }} bgGradient="linear(to-r, #3B82F6, #2563EB)" bgClip="text" mb={2} fontWeight="bold">
                  Накладні продажу
                </Heading>
                <Text color={textColor} fontSize={{ base: 'sm', md: 'lg' }}>
                  Управління накладними продажу
                </Text>
              </Box>
            </Flex>
            <Button
              size={{ base: 'md', md: 'lg' }}
              bg="linear-gradient(135deg, #10B981, #059669)"
              color="white"
              leftIcon={<AddIcon />}
              onClick={handleCreateClick}
              borderRadius="xl"
              px={{ base: 6, md: 8 }}
              boxShadow="0 4px 15px rgba(16, 185, 129, 0.4)"
              _hover={{ bg: 'linear-gradient(135deg, #059669, #047857)', transform: { base: 'none', md: 'translateY(-2px)' }, boxShadow: '0 8px 25px rgba(16, 185, 129, 0.6)', transition: 'all 0.2s ease' }}
              _active={{ transform: 'translateY(0)' }}
              fontWeight="600"
            >
              Створити накладну
            </Button>
          </Flex>
        </Box>
        {/* Modern Filters Card */}
        <Box
          bg={bgColor}
          borderRadius={{ base: 'lg', md: '2xl' }}
          border="1px solid"
          borderColor={borderColor}
          p={{ base: 3, md: 6 }}
          mb={{ base: 4, md: 8 }}
          boxShadow="0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
          position="relative"
          overflow="hidden"
        >
          {/* Decorative gradient overlay - Purple theme for filters */}
          <Box position="absolute" top={0} left={0} right={0} height="4px" bg="linear-gradient(90deg, #8B5CF6, #7C3AED, #6D28D9)" opacity={0.8} />
          
          <VStack spacing={4} align="stretch">
            <Text 
              fontSize="lg" 
              fontWeight="bold"
              bgGradient="linear(to-r, #8B5CF6, #7C3AED)"
              bgClip="text"
            >
              🔍 Фільтри та пошук
              {(filters.search || filters.start_date || filters.end_date || filters.total_min || filters.total_max || filters.status) && (
                <Text as="span" fontSize="sm" color="green.500" ml={2}>
                  (активні)
                </Text>
              )}
            </Text>
            
            {/* First row - Search */}
            <Box>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color={textColor} mb={2}>
                  Пошук
                </FormLabel>
                <Input
                  placeholder="🔍 Пошук за ID накладної, назвою товару, каталожним номером, клієнтом, статусом..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  bg={bgColor}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  _focus={{
                    borderColor: "#8B5CF6",
                    boxShadow: "0 0 0 1px rgba(139, 92, 246, 0.3)",
                  }}
                  _placeholder={{
                    color: textColor,
                  }}
                />
              </FormControl>
            </Box>
            
            {/* Second row - Date, Amount and Status filters */}
            <HStack spacing={4} align="end">
              <FormControl flex="1">
                <FormLabel fontSize="sm" fontWeight="600" color={textColor} mb={2}>
                  📅 Дата від
                </FormLabel>
                <Input
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => handleFilterChange('start_date', e.target.value)}
                  bg={bgColor}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  _focus={{
                    borderColor: "#8B5CF6",
                    boxShadow: "0 0 0 1px rgba(139, 92, 246, 0.3)",
                  }}
                />
              </FormControl>
              
              <FormControl flex="1">
                <FormLabel fontSize="sm" fontWeight="600" color={textColor} mb={2}>
                  📅 Дата до
                </FormLabel>
                <Input
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => handleFilterChange('end_date', e.target.value)}
                  bg={bgColor}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  _focus={{
                    borderColor: "#8B5CF6",
                    boxShadow: "0 0 0 1px rgba(139, 92, 246, 0.3)",
                  }}
                />
              </FormControl>
              
              <FormControl flex="1">
                <FormLabel fontSize="sm" fontWeight="600" color={textColor} mb={2}>
                  💰 Сума від (грн)
                </FormLabel>
                <Input
                  type="number"
                  placeholder="Мін. сума"
                  value={filters.total_min}
                  onChange={(e) => handleFilterChange('total_min', e.target.value)}
                  bg={bgColor}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  _focus={{
                    borderColor: "#8B5CF6",
                    boxShadow: "0 0 0 1px rgba(139, 92, 246, 0.3)",
                  }}
                />
              </FormControl>
              
              <FormControl flex="1">
                <FormLabel fontSize="sm" fontWeight="600" color={textColor} mb={2}>
                  💰 Сума до (грн)
                </FormLabel>
                <Input
                  type="number"
                  placeholder="Макс. сума"
                  value={filters.total_max}
                  onChange={(e) => handleFilterChange('total_max', e.target.value)}
                  bg={bgColor}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  _focus={{
                    borderColor: "#8B5CF6",
                    boxShadow: "0 0 0 1px rgba(139, 92, 246, 0.3)",
                  }}
                />
              </FormControl>
              
              <FormControl flex="1">
                <FormLabel fontSize="sm" fontWeight="600" color={textColor} mb={2}>
                  📋 Статус
                </FormLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  bg={bgColor}
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="lg"
                  _focus={{
                    borderColor: "#8B5CF6",
                    boxShadow: "0 0 0 1px rgba(139, 92, 246, 0.3)",
                  }}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
              
              <Button 
                onClick={clearFilters}
                bg="linear-gradient(135deg, #EF4444, #DC2626)"
                color="white"
                borderRadius="lg"
                px={6}
                py={2}
                boxShadow="0 4px 6px rgba(239, 68, 68, 0.3)"
                _hover={{
                  bg: "linear-gradient(135deg, #DC2626, #B91C1C)",
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 15px rgba(239, 68, 68, 0.4)",
                }}
                transition="all 0.2s"
                height="40px"
              >
                🗑️ Очистити
              </Button>
            </HStack>
          </VStack>
        </Box>

        {/* Content Area */}
          {loading ? (
            <Flex justify="center" align="center" minH="400px">
              <VStack spacing={4}>
                <Spinner size="xl" color="blue.500" thickness="4px" />
                <Text color={textColor} fontSize="lg">
                  Завантаження накладних...
                </Text>
              </VStack>
            </Flex>
          ) : invoices.length ? (
            <InvoiceList
              invoices={invoices}
              onEdit={handleEditInvoice}
              onDelete={handleDeleteInvoice}
              onViewDetails={handleViewDetails}
              onPrintInvoice={handlePrintInvoice}
              onChangeStatus={handleChangeStatus}
            />
          ) : (
            <Flex justify="center" align="center" minH="400px">
              <VStack spacing={6}>
                <Box
                  p={6}
                  bg="linear-gradient(135deg, #EF4444, #DC2626)"
                  borderRadius="full"
                  boxShadow="0 4px 6px -1px rgba(239, 68, 68, 0.3)"
                >
                  <Icon viewBox="0 0 24 24" boxSize={12} color="white">
                    <path fill="currentColor" d="M19,13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
                  </Icon>
                </Box>
                <VStack spacing={2}>
                  <Text fontSize="xl" fontWeight="bold" color={textColor}>
                    Повернень не знайдено
                  </Text>
                  <Text color={textColor} textAlign="center" maxW="md">
                    {(filters.search || filters.start_date || filters.end_date || filters.total_min || filters.total_max || filters.status) 
                      ? "За вашими критеріями пошуку накладних не знайдено. Спробуйте змінити фільтри."
                      : "У системі ще немає жодної накладної повернення. Створіть першу накладну!"
                    }
                  </Text>
                  <Button
                    mt={4}
                    bg="linear-gradient(135deg, #10B981, #059669)"
                    color="white"
                    leftIcon={<AddIcon />}
                    onClick={handleCreateClick}
                    borderRadius="lg"
                    px={6}
                    boxShadow="0 4px 15px rgba(16, 185, 129, 0.4)"
                    _hover={{
                      bg: "linear-gradient(135deg, #059669, #047857)",
                      transform: "translateY(-2px)",
                      boxShadow: "0 8px 25px rgba(16, 185, 129, 0.6)",
                    }}
                  >
                    Створити накладну
                  </Button>
                </VStack>
              </VStack>
            </Flex>
          )}

        {/* Modern Pagination - Responsive */}
        {pagination.count > 0 && (
          <Box bg={bgColor} borderRadius="2xl" border="1px solid" borderColor={borderColor} p={{ base: 4, md: 6 }} mt={{ base: 6, md: 8 }} boxShadow="0 10px 15px -3px rgba(0, 0, 0, 0.1)">
            <Flex justify="center" align="center">
              {pagination.count > 10 && (
                <HStack spacing={{ base: 2, md: 3 }} flexWrap="wrap" justify="center">
                  <Button onClick={() => fetchPrevious()} isDisabled={!pagination.previous || pagination.currentPage <= 1} bg="linear-gradient(135deg, #3B82F6, #2563EB)" color="white" borderRadius="xl" size={{ base: 'sm', md: 'md' }} px={{ base: 3, md: 6 }} _hover={{ bg: 'linear-gradient(135deg, #2563EB, #1D4ED8)', transform: { base: 'none', md: 'translateY(-1px)' } }} _disabled={{ bg: 'gray.300', color: 'gray.500', transform: 'none', cursor: 'not-allowed', opacity: 0.6 }}>
                    ← Попередня
                  </Button>
                  {Array.from({ length: Math.ceil(pagination.count / 10) }, (_, index) => (
                    <Button key={index + 1} onClick={() => goToPage(index + 1)} size={{ base: 'sm', md: 'md' }} minW={{ base: '36px', md: '40px' }} px={{ base: 2, md: 3 }} bg={pagination.currentPage === index + 1 ? 'linear-gradient(135deg, #10B981, #059669)' : bgColor} color={pagination.currentPage === index + 1 ? 'white' : textColor} border="1px solid" borderColor={pagination.currentPage === index + 1 ? 'transparent' : borderColor} borderRadius="lg" _hover={{ bg: pagination.currentPage === index + 1 ? 'linear-gradient(135deg, #059669, #047857)' : borderColor, transform: { base: 'none', md: 'translateY(-1px)' } }}>
                      {index + 1}
                    </Button>
                  ))}
                  <Button onClick={() => fetchNext()} isDisabled={!pagination.next} bg="linear-gradient(135deg, #3B82F6, #2563EB)" color="white" borderRadius="xl" size={{ base: 'sm', md: 'md' }} px={{ base: 3, md: 6 }} _hover={{ bg: 'linear-gradient(135deg, #2563EB, #1D4ED8)', transform: { base: 'none', md: 'translateY(-1px)' } }} _disabled={{ bg: 'gray.300', color: 'gray.500', transform: 'none', cursor: 'not-allowed', opacity: 0.6 }}>
                    Наступна →
                  </Button>
                </HStack>
              )}
            </Flex>
          </Box>
        )}

        {/* Create/Edit Modal */}
        <InvoiceModal
          key={modalKey}
          isOpen={isCreateModalOpen}
          onClose={handleCloseModal}
          type={"sell"}
          invoiceData={editingInvoice}
          onInvoiceCreated={handleInvoiceCreated}
        />

        {/* Invoice Details Modal */}
        {selectedInvoice && (
          <Modal
            isOpen={isDetailsModalOpen}
            onClose={handleCloseDetailsModal}
            size="4xl"
            isCentered
          >
            <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
            <ModalContent>
              <ModalHeader>Деталі накладної</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                {selectedInvoice ? (
                <Box>
                  {/* Invoice Header Info - Always show this immediately */}
                  <Box
                    bg="blue.50"
                    p={4}
                    borderRadius="xl"
                    mb={6}
                    border="1px solid"
                    borderColor="blue.200"
                  >
                    <Text fontWeight="bold" fontSize="lg" color="blue.700" mb="2">
                      Накладна #{selectedInvoice.id}
                    </Text>
                    <Text color="gray.700">
                      Дата створення: {new Date(selectedInvoice.created_at).toLocaleString()}
                    </Text>
                    <Text color="gray.700">
                      Клієнт: {selectedInvoice.customer_name || 'Без клієнта'}
                    </Text>
                    <Text color="gray.700">
                      Примітка: {selectedInvoice.note || 'Без приміток'}
                    </Text>
                  </Box>

                  {/* Product List */}
                  {selectedInvoice.entries && selectedInvoice.entries.length > 0 ? (
                  <Box
                    maxH="400px"
                    overflowY="auto"
                    borderWidth="1px"
                    borderRadius="lg"
                    borderColor={borderColor}
                    bg={bgColor}
                  >
                    <Table variant="simple" size="sm">
                      <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                        <Tr>
                          <Th>Назва товару</Th>
                          <Th>Закупочна ціна</Th>
                          <Th>Відсоток націнки</Th>
                          <Th>Ціна за одиницю</Th>
                          <Th>Кількість</Th>
                          <Th>Загальна ціна</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {selectedInvoice.entries.map((entry) => (
                          <Tr key={entry.id}>
                            <Td fontWeight="medium">
                              {entry.product.name}
                            </Td>
                            <Td>
                              {entry.purchase_price} грн
                            </Td>
                            <Td>
                              {entry.markup_percentage} %
                            </Td>
                            <Td>
                              {entry.price} грн
                            </Td>
                            <Td>
                              {entry.quantity}
                            </Td>
                            <Td fontWeight="semibold">
                              {(parseFloat(entry.price) * parseInt(entry.quantity)).toFixed(2)} грн
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                  ) : (
                    <Box textAlign="center" p={8}>
                      <Text color="gray.700">
                        Немає товарів у цій накладній
                      </Text>
                    </Box>
                  )}

                  {/* Totals */}
                  {selectedInvoice.entries && selectedInvoice.entries.length > 0 && (
                    <Box 
                      textAlign="right" 
                      mt="6" 
                      borderTop="1px solid"
                      borderColor={borderColor}
                      pt="4"
                    >
                      <Box
                        bg="green.50"
                        p={4}
                        borderRadius="lg"
                        border="1px solid"
                        borderColor="green.200"
                      >
                        <Text color="gray.700" mb={1}>
                          Разом без ПДВ: {totalWithoutVat.toFixed(2)} грн
                        </Text>
                        <Text color="gray.700" mb={1}>
                          ПДВ: 0.00 грн
                        </Text>
                        <Text 
                          fontWeight="bold" 
                          fontSize="lg"
                          color="green.700"
                        >
                          Всього з ПДВ: {totalWithoutVat.toFixed(2)} грн
                        </Text>
                      </Box>
                    </Box>
                  )}
                </Box>
                ) : (
                  <Flex justify="center" align="center" minH="400px">
                    <Box textAlign="center">
                      <Spinner 
                        size="xl" 
                        color="blue.500"
                        thickness="4px"
                        mb={4}
                      />
                      <Text color="gray.700">
                        Завантаження накладної...
                      </Text>
                    </Box>
                  </Flex>
                )}
              </ModalBody>
            </ModalContent>
          </Modal>
        )}
      </Box>
    </Box>
  );
};

export default SellInvoicesPage;
